import { useState, type KeyboardEvent } from "react";

/**
 * Lightweight tag editor. Type to add (Enter or comma commits). Click × to
 * remove. Used for specialisations on roles + users; could be reused for
 * any list-of-strings input in future.
 */
export function TagInput({
  value, onChange, placeholder, suggestions = [],
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  suggestions?: string[];
}) {
  const [draft, setDraft] = useState("");

  function add(raw: string) {
    const tag = raw.trim().toLowerCase();
    if (!tag) return;
    if (value.includes(tag)) return;
    onChange([...value, tag].slice(0, 20));
    setDraft("");
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(draft);
    } else if (e.key === "Backspace" && !draft && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  return (
    <div>
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 6,
        padding: 6, border: "1px solid var(--border)", borderRadius: 6,
        background: "var(--panel)", minHeight: 38, alignItems: "center",
      }}>
        {value.map((tag) => (
          <span key={tag} style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            background: "var(--accent-soft)", color: "var(--accent)",
            padding: "2px 8px", borderRadius: 999, fontSize: 12, fontWeight: 500,
          }}>
            {tag}
            <button
              type="button"
              aria-label={`remove ${tag}`}
              onClick={() => onChange(value.filter((t) => t !== tag))}
              style={{
                background: "transparent", border: "none", color: "inherit",
                cursor: "pointer", fontSize: 14, padding: 0, lineHeight: 1,
              }}
            >×</button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          placeholder={value.length === 0 ? (placeholder ?? "Type and press Enter…") : ""}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          style={{
            flex: 1, minWidth: 120, border: "none", background: "transparent",
            outline: "none", padding: "4px",
          }}
        />
      </div>
      {suggestions.length > 0 && (
        <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
          {suggestions
            .filter((s) => !value.includes(s))
            .slice(0, 8)
            .map((s) => (
              <button
                key={s}
                type="button"
                className="ghost"
                onClick={() => add(s)}
                style={{
                  fontSize: 11, padding: "2px 8px", borderRadius: 999,
                  border: "1px dashed var(--border)", color: "var(--muted)",
                  background: "transparent", cursor: "pointer",
                }}
              >+ {s}</button>
            ))}
        </div>
      )}
    </div>
  );
}

export const COMMON_SPECIALISATIONS = [
  "surveillance",
  "field-recruitment",
  "cyber",
  "social-media",
  "rally-operations",
  "communications",
  "logistics",
  "legal",
  "financial-tracking",
  "youth-mobilisation",
  "women's-mobilisation",
  "media-relations",
  "investigation",
  "analysis",
];
